import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, Observer, Subscription } from 'rxjs';
import { finalize, take } from 'rxjs/operators';
import { EnumStepFunctions } from '../shared/enums/step-functions.enum';
import { IWebSocketParams } from '../shared/interfaces/web-socket-params.interface';
import { CustomerDataService } from '../shared/services/customer-data.service';
import { LoadingService } from '../shared/services/loading.service';
import { WebSocketService } from '../shared/services/web-socket.service';
import { routesData } from '../shared/resources/routes.constant';
import { AnalyticService } from '../shared/services/analytic.service';

@Injectable({
  providedIn: 'root'
})
export class CrmCdtAccountsCustomerService {
  stepFunctionSub: Subscription = new Subscription();

  constructor(
    public webSocketService: WebSocketService,
    public customerData: CustomerDataService,
    private loadingService: LoadingService,
    private router: Router,
    public analytic: AnalyticService
  ) {}

  connectWebSocket(webSocketParams: IWebSocketParams): void {
    this.webSocketService.wsBuild(webSocketParams);
  }

  allStepFunctionSubscribe(): void {
    const arraySteps = [
      EnumStepFunctions.StepReadCrm,
      EnumStepFunctions.StepAccountsCustomer,
      EnumStepFunctions.StepCreateCdt,
      EnumStepFunctions.StepSaveSummaryCdt,
      EnumStepFunctions.StepEndProcess
    ];

    arraySteps.forEach((step: EnumStepFunctions) => {
      this.webSocketService.wsGetMessages$(step).subscribe({
        next: (message) => {
          const resultStep = message[step];
          if (resultStep) {
            if (EnumStepFunctions.StepEndProcess !== step) {
              if (
                resultStep.statusCode === 200 ||
                resultStep.statusCode === 201
              ) {
                const resultParse = JSON.parse(resultStep.body);
                this.customerData.customerConditionData = {
                  ...this.customerData.customerConditionData,
                  ...{ [step]: resultParse }
                };
              } else {
                this.webSocketService.wsComplete();
                this.analytic.uTagLinkError(
                  'Error WS ' + step + ' statusCode: ' + resultStep.statusCode,
                  'error_tech_page'
                );
                this.router.navigate([
                  routesData.ERRORS.GENERAL_ERROR.redirectPath
                ]);
              }
            } else {
              this.webSocketService.wsComplete();
            }
          } else {
            this.webSocketService.wsComplete();
            this.analytic.uTagLinkError('Error WS ' + step, 'error_tech_page');
            this.router.navigate([
              routesData.ERRORS.GENERAL_ERROR.redirectPath
            ]);
          }
        },
        error: (error) => {
          let errorMessage = '';
          if (error?.message !== undefined) {
            errorMessage = error.message;
          }
          this.analytic.uTagLinkError(
            'Error WS ' + step + ' ' + errorMessage,
            'error_tech_page'
          );
          this.router.navigate([routesData.ERRORS.GENERAL_ERROR.redirectPath]);
        }
      });
    });
  }

  stepFunctionSubscribe(
    observer: Observer<any>,
    step: EnumStepFunctions
  ): void {
    this.loadingService.startLoading();

    this.stepFunctionSub = this.webSocketService
      .wsGetMessages$(step)
      .pipe(
        take(1),
        finalize(() => this.loadingService.stopLoading())
      )
      .subscribe({
        next: (message) => {
          const resultStep = message[step];

          if (resultStep) {
            if (
              resultStep.statusCode === 200 ||
              resultStep.statusCode === 201
            ) {
              const resultParse = JSON.parse(resultStep.body);
              this.customerData.customerConditionData = {
                ...this.customerData.customerConditionData,
                ...{ [step]: resultParse }
              };
              observer.next(this.customerData.customerConditionData[step]);
              observer.complete();
            } else {
              observer.error(resultStep.statusCode);
            }
          } else {
            observer.error(`${step} undefined`);
          }
        },
        error: (error) => {
          observer.error(error);
        },
        complete: () => {
          observer.complete();
        }
      });
  }

  observerStepFunctionData$(step: EnumStepFunctions): Observable<any> {
    return new Observable<any>((observer) => {
      if (this.customerData.customerConditionData[step]) {
        observer.next(this.customerData.customerConditionData[step]);
        observer.complete();
      } else {
        this.stepFunctionSubscribe(observer, step);
      }
    }).pipe(
      take(1),
      finalize(() => this.killSubscription())
    );
  }

  killSubscription(): void {
    if (this.stepFunctionSub && !this.stepFunctionSub.closed) {
      this.stepFunctionSub.unsubscribe();
    }
  }

  sendMessage(msj: any) {
    this.webSocketService.wsSendMessage(msj);
    this.allStepFunctionSubscribe();
  }

  waitStepEndProcess(): Observable<any> {
    this.loadingService.startLoading();
    return this.webSocketService
      .wsGetMessages$(EnumStepFunctions.StepEndProcess)
      .pipe(
        take(1),
        finalize(() => {
          this.loadingService.stopLoading();
          this.webSocketService.wsComplete();
        })
      );
  }
}
